import { EStatusDoc } from '@configs/interface.config'

export enum EItemCodeGroupBasis {
  AREA_M2 = 'AREA_M2',
  LENGTH_MD = 'LENGTH_MD',
  REVENUE_VND = 'REVENUE_VND',
}

// Trục nhận diện sản phẩm của nhóm (ADR-037) — thuộc tính của NHÓM, không của từng dòng member.
// Một nhóm thuần một trục, không lai.
export enum EItemCodeGroupMemberType {
  ITEM_GROUP_CODE = 'ITEM_GROUP_CODE',
  COLOR_CODE = 'COLOR_CODE',
  ITEM_CODE = 'ITEM_CODE',
}

export enum EPolicyVersionStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  CLOSED = 'CLOSED',
}

export type TPolicyVersionRef = {
  effectiveFrom: number
  status: EPolicyVersionStatus
}

export type TItemCodeGroup = {
  id: number
  code: string
  name: string
  displayName: string
  memberType: EItemCodeGroupMemberType
  basis: EItemCodeGroupBasis
  revenueUnitVnd: number | null
  sortOrder: number
  note: string | null
  status: EStatusDoc
  memberCount: number
  createdAt: string
  updatedAt: string
}

export type TItemCodeGroupDetail = TItemCodeGroup & {
  usedByPolicyVersions: TPolicyVersionRef[]
  // Tổng số vật tư trong catalogue hiện resolve vào nhóm này — con số ops thực sự quan tâm,
  // khác memberCount (số mã nhóm, ≤34).
  itemCount: number
  staleMemberCount: number
}

export type TCreateItemCodeGroup = {
  code: string
  name: string
  displayName: string
  // Bỏ trống ⇒ BE mặc định ITEM_GROUP_CODE (hành vi y hệt trước ADR-037).
  memberType?: EItemCodeGroupMemberType
  basis: EItemCodeGroupBasis
  revenueUnitVnd: number | null
  sortOrder?: number
  note?: string | null
  status?: EStatusDoc
}

export type TUpdateItemCodeGroup = TCreateItemCodeGroup

export type TQueryItemCodeGroup = {
  page: number
  limit: number
  search?: string
  status?: EStatusDoc
  basis?: EItemCodeGroupBasis
  memberType?: EItemCodeGroupMemberType
}

// Thrown by DELETE :id / PUT :id when a PUBLISHED/CLOSED policy version locks the group —
// the body is the raw NestJS ConflictException payload, not the generic {message} shape.
export type TItemCodeGroupLockedError = {
  message: string
  usedByPolicyVersions: TPolicyVersionRef[]
}

// ─── Members ───────────────────────────────────────────────────────────────
// v3.0 (ADR-037): giá trị thành viên diễn giải theo `memberType` của nhóm — mã nhóm hàng (≤34),
// mã màu (≤251) hay mã sản phẩm (67.909, trục duy nhất phân trang/search server-side).

export type TItemCodeGroupMember = {
  id: number
  memberValue: string
  // Số vật tư trong catalogue khớp giá trị này ở trục của nhóm. Tính lúc đọc, không lưu cột.
  itemCount: number
  existsInCatalog: boolean
  createdAt: string
}

// Nguồn tick trục ITEM_GROUP_CODE — các giá trị item.itemGroupCode có thật trong catalogue.
export type TCatalogItemGroup = {
  itemGroupCode: string
  itemCount: number
  activeItemCount: number
  // Các nhóm memberType=ITEM_GROUP_CODE đã chứa mã này — cảnh báo chồng lấn tại chỗ (BR-ICG-05).
  assignedToGroupIds: number[]
}

// Nguồn tick trục COLOR_CODE — DISTINCT item.colorCode, KHÔNG đọc bảng `color` (ADR-037 §2.3).
export type TCatalogColor = {
  colorCode: string
  itemCount: number
  activeItemCount: number
  assignedToGroupIds: number[]
}

// Nguồn tick trục ITEM_CODE — có phân trang, không phải danh sách đóng.
export type TCatalogItem = {
  code: string
  name: string
  itemGroupCode: string
  colorCode: string | null
  isActive: boolean
  assignedToGroupIds: number[]
}

export type TQueryCatalogItems = {
  page: number
  limit: number
  search?: string
}

export type TPutItemCodeGroupMembers = {
  memberValues: string[]
}

export type TPutItemCodeGroupMembersResult = {
  added: number
  removed: number
  memberCount: number
}

// ─── Overlaps (chồng lấn mức sản phẩm, hạ đơn vị xuống `item` — ADR-037 §4) ─────────────────────

export type TItemCodeGroupOverlapSample = {
  itemCode: string
  itemName: string
  groupIds: number[]
}

export type TItemCodeGroupOverlapsResult = {
  total: number
  samples: TItemCodeGroupOverlapSample[]
}
